// src/pages/AxiomPoliciesV3.jsx
// AXIOM Intelligence Platform — Policy Engine · Light Institutional Skin
// Matches AxiomPortfolioV3: warm white · charcoal · gold · Georgia serif

import React, { useState } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — identical to V3
// ════════════════════════════════════════════════════════════════════════════
const T = {
  bg:            '#F5F3EF',
  bgCard:        '#FFFFFF',
  bgElevated:    '#FAFAF8',
  bgHover:       '#F0EDE8',
  border:        '#E2DDD6',
  borderStrong:  '#C8C2B8',
  textPrimary:   '#1A1814',
  textSecondary: '#5C5850',
  textMuted:     '#9C9890',
  gold:          '#9A7B2E',
  goldLight:     '#C4992A',
  goldBg:        'rgba(154,123,46,0.06)',
  goldBorder:    'rgba(154,123,46,0.18)',
  red:           '#8B1A1A',
  redLight:      '#B32020',
  redBg:         'rgba(139,26,26,0.05)',
  redBorder:     'rgba(139,26,26,0.15)',
  green:         '#1A4D2E',
  greenLight:    '#1E6B3A',
  greenBg:       'rgba(26,77,46,0.05)',
  greenBorder:   'rgba(26,77,46,0.15)',
  amber:         '#7A5C0A',
  amberLight:    '#9A7210',
  amberBg:       'rgba(122,92,10,0.05)',
  amberBorder:   'rgba(122,92,10,0.15)',
  navy:          '#1A2D5A',
  navyBg:        'rgba(26,45,90,0.05)',
  navyBorder:    'rgba(26,45,90,0.15)',
};

// ════════════════════════════════════════════════════════════════════════════
// CATEGORY CONFIG
// ════════════════════════════════════════════════════════════════════════════
const CATEGORIES = ['All', 'Site', 'Build', 'Commission', 'Capital', 'Exit'];

const CAT = (cat) => ({
  Site:       { color: '#2E6EA6', bg: 'rgba(46,110,166,0.06)',  border: 'rgba(46,110,166,0.18)',  dot: '#2E6EA6'  },
  Build:      { color: '#9A7B2E', bg: 'rgba(154,123,46,0.06)', border: 'rgba(154,123,46,0.18)', dot: '#9A7B2E'  },
  Commission: { color: '#8B1A1A', bg: 'rgba(139,26,26,0.05)',  border: 'rgba(139,26,26,0.15)',  dot: '#8B1A1A'  },
  Capital:    { color: '#1A2D5A', bg: 'rgba(26,45,90,0.05)',   border: 'rgba(26,45,90,0.15)',   dot: '#1A2D5A'  },
  Exit:       { color: '#5B4E8A', bg: 'rgba(91,78,138,0.05)',  border: 'rgba(91,78,138,0.15)',  dot: '#5B4E8A'  },
}[cat] || { color: T.textMuted, bg: 'transparent', border: T.border, dot: T.textMuted });

const STATUS = (status) => ({
  Triggered:  { label: 'Triggered',  dot: T.redLight,   text: T.red,        bg: T.redBg,   border: T.redBorder   },
  Active:     { label: 'Active',     dot: T.greenLight, text: T.greenLight, bg: T.greenBg, border: T.greenBorder },
  Monitoring: { label: 'Monitoring', dot: T.amberLight, text: T.amber,      bg: T.amberBg, border: T.amberBorder },
  Inactive:   { label: 'Inactive',   dot: T.textMuted,  text: T.textMuted,  bg: T.bg,      border: T.border      },
}[status] || {});

// ════════════════════════════════════════════════════════════════════════════
// POLICY DATA
// ════════════════════════════════════════════════════════════════════════════
const POLICIES = [
  {
    id: 'SITE-001', name: 'Grid Queue Threshold', category: 'Site', status: 'Triggered',
    description: 'Fires when utility interconnect queue exceeds corpus median by more than 20% for the substation class and jurisdiction.',
    trigger: 'Interconnect queue > corpus median + 20%',
    corpusBasis: '31 site selections across $5B portfolio — APS, PG&E, ComEd, Dominion, ERCOT substations.',
    confidenceThreshold: 80,
    recommendedAction: 'Advance utility pre-application immediately. Model alternative substations within 15-mile radius. Notify BOD of timeline risk.',
    lastFired: '3 days ago', lastFireProject: 'Sonoran Compute Park',
    projectsAssigned: ['Sonoran Compute Park', 'Mile High AI Hub', 'Midwest Sovereign Exchange'],
    projectsTriggered: ['Sonoran Compute Park'],
    savedValue: '$18M',
    corpusMatches: [
      { project: 'Switch PHX 1',    year: 2019, outcome: 'Same APS substation — 22-month queue, project relocated', relevance: 95 },
      { project: 'QTS Chandler',    year: 2021, outcome: 'Pre-application advanced 5 months, queue reduced to 14 months', relevance: 89 },
      { project: 'Aligned Gilbert', year: 2023, outcome: 'Alternative feed identified, saved 6 months vs primary', relevance: 83 },
    ],
  },
  {
    id: 'SITE-002', name: 'Latency Spec Validation', category: 'Site', status: 'Triggered',
    description: 'Validates fiber routing from site to tenant-required exchange or cloud on-ramp. Fires when measured latency exceeds tenant SLA.',
    trigger: 'Modeled latency > tenant spec threshold',
    corpusBasis: '19 financial exchange and latency-sensitive deployments. Corpus includes 6 fiber routing failures with known remediation costs.',
    confidenceThreshold: 85,
    recommendedAction: 'Model 3 fiber route options immediately. Identify dark fiber procurement path before BOD lock. Cost of resolution scales 4× after steel is set.',
    lastFired: '1 day ago', lastFireProject: 'Midwest Sovereign Exchange',
    projectsAssigned: ['Midwest Sovereign Exchange', 'Mile High AI Hub'],
    projectsTriggered: ['Midwest Sovereign Exchange'],
    savedValue: '$12M',
    corpusMatches: [
      { project: 'Equinix CH6',       year: 2019, outcome: 'Dark fiber resolved constraint — $2M vs $14M rework', relevance: 91 },
      { project: 'Iron Mountain DEN', year: 2020, outcome: 'Latency conflict — site abandoned, $6M sunk cost', relevance: 88 },
      { project: 'QTS Chicago',       year: 2022, outcome: 'Resolved at BOD via design addendum, zero impact', relevance: 86 },
    ],
  },
  {
    id: 'SITE-003', name: 'Water Rights Availability', category: 'Site', status: 'Active',
    description: 'Confirms adequate water rights for cooling load at projected MW capacity before site commitment.',
    trigger: 'Water allocation < cooling load at projected MW + 15% buffer',
    corpusBasis: '24 site selections in water-constrained markets: Phoenix, Las Vegas, Denver, Dallas, Atlanta.',
    confidenceThreshold: 90,
    recommendedAction: 'Confirm allocation before LOI. File water rights application concurrently with utility pre-application.',
    lastFired: '14 days ago', lastFireProject: 'Mile High AI Hub',
    projectsAssigned: ['Mile High AI Hub', 'Sonoran Compute Park'],
    projectsTriggered: [],
    savedValue: '$8M',
    corpusMatches: [
      { project: 'Aligned PHX 1', year: 2021, outcome: 'Water rights secured concurrently — no delay', relevance: 93 },
      { project: 'Aligned DEN 1', year: 2022, outcome: 'Upfront rights secured — no renegotiation at construction', relevance: 84 },
    ],
  },
  {
    id: 'SITE-004', name: 'Permitting Risk Scoring', category: 'Site', status: 'Monitoring',
    description: 'Scores permitting risk by jurisdiction based on corpus history of approval timelines, opposition patterns, and environmental review requirements.',
    trigger: 'Permitting risk score < 65 for target jurisdiction',
    corpusBasis: 'Permitting history across 38 jurisdictions. Includes 11 permit delays with causal analysis.',
    confidenceThreshold: 75,
    recommendedAction: 'Engage local government relations 6 months before permit filing. Pre-engage opposition stakeholders.',
    lastFired: '22 days ago', lastFireProject: 'Sonoran Compute Park',
    projectsAssigned: ['Sonoran Compute Park', 'Mile High AI Hub', 'Lone Star AI Campus'],
    projectsTriggered: [],
    savedValue: '$6M avg',
    corpusMatches: [
      { project: 'CyrusOne Austin',  year: 2021, outcome: 'Opposition delay 8 months — early engagement would have prevented', relevance: 87 },
      { project: 'Aligned Garland',  year: 2023, outcome: 'Permit secured in 90 days via pre-engagement protocol', relevance: 82 },
    ],
  },
  {
    id: 'BILD-001', name: 'Vendor Lead Time Deviation', category: 'Build', status: 'Triggered',
    description: 'Monitors critical path equipment lead times vs contract. Fires when any single vendor shows >8-week deviation across the portfolio.',
    trigger: 'Equipment lead time deviation > +8 weeks vs contract',
    corpusBasis: '47 build phases across $5B portfolio. Tracks 12 critical equipment categories.',
    confidenceThreshold: 87,
    recommendedAction: 'Authorize parallel procurement from secondary vendor. Single procurement decision can protect multiple sites simultaneously.',
    lastFired: '2 days ago', lastFireProject: 'Peachtree Edge Data',
    projectsAssigned: ['Lone Star AI Campus', 'Peachtree Edge Data', 'Sonoran Compute Park'],
    projectsTriggered: ['Peachtree Edge Data'],
    savedValue: '$28M (3 sites)',
    corpusMatches: [
      { project: 'Switch LAS 2022', year: 2022, outcome: 'Vertiv delay — 14-week slip, $22M penalty. No parallel procurement.', relevance: 97 },
      { project: 'eBay PHX 2019',   year: 2019, outcome: 'Early detection — Eaton substitution, zero schedule impact', relevance: 94 },
      { project: 'CyrusOne ATL',    year: 2021, outcome: 'Dual-source procurement standard implemented after incident', relevance: 81 },
    ],
  },
  {
    id: 'BILD-002', name: 'Portfolio Equipment Pattern', category: 'Build', status: 'Triggered',
    description: 'Detects the same equipment failure, delay, or defect pattern across 2+ portfolio sites simultaneously — the cross-portfolio signal no single PM sees.',
    trigger: 'Same equipment SKU + delay signature at ≥2 active sites',
    corpusBasis: 'Cross-portfolio correlation engine trained on $5B of equipment procurement, delivery, and substitution history.',
    confidenceThreshold: 85,
    recommendedAction: 'Treat as portfolio-level emergency. Single escalation and procurement decision protects all affected sites.',
    lastFired: '2 days ago', lastFireProject: '3 sites (Dallas, Atlanta, Seattle)',
    projectsAssigned: ['Lone Star AI Campus', 'Peachtree Edge Data'],
    projectsTriggered: ['Peachtree Edge Data'],
    savedValue: '$28M combined',
    corpusMatches: [
      { project: 'Aligned Portfolio 2022', year: 2022, outcome: 'Detected Generac delay across 4 sites — consolidated procurement saved 8 weeks', relevance: 94 },
      { project: 'Switch Multi-site 2021', year: 2021, outcome: 'Same Vertiv UPS SKU — early detection enabled substitution', relevance: 90 },
    ],
  },
  {
    id: 'BILD-003', name: 'RFI Escalation Threshold', category: 'Build', status: 'Monitoring',
    description: 'Tracks open RFI count, age, and criticality. Fires when open P1 RFIs exceed threshold — a leading indicator of commissioning risk.',
    trigger: 'Open P1 RFIs > 25 with >14 days average age during Build phase',
    corpusBasis: '39 build phases with RFI tracking. P1 RFI count at Build 70% is the single strongest predictor of commissioning slip.',
    confidenceThreshold: 82,
    recommendedAction: 'Convene RFI triage session within 48 hours. Prioritize top 10 P1s for immediate resolution.',
    lastFired: '5 days ago', lastFireProject: 'Peachtree Edge Data',
    projectsAssigned: ['Peachtree Edge Data', 'Lone Star AI Campus', 'Midwest Sovereign Exchange'],
    projectsTriggered: [],
    savedValue: '$4M avg',
    corpusMatches: [
      { project: 'eBay SLC 2018',     year: 2018, outcome: '42 open P1 RFIs at Build 65% — commissioning slipped 18 days', relevance: 91 },
      { project: 'Aligned PHX Build', year: 2023, outcome: 'RFI triage at week 32 — no commissioning impact', relevance: 85 },
    ],
  },
  {
    id: 'BILD-004', name: 'MEP vs Schedule Variance', category: 'Build', status: 'Active',
    description: 'Monitors mechanical, electrical, and plumbing progress against milestone schedule. Flags variance before it compounds into commissioning risk.',
    trigger: 'MEP progress > 8% behind schedule milestone',
    corpusBasis: 'Schedule tracking across 31 build phases. MEP lag at 50% build milestone has 78% correlation with commissioning slippage.',
    confidenceThreshold: 78,
    recommendedAction: 'Increase crew density on lagging MEP trades. Evaluate weekend shift authorization.',
    lastFired: '8 days ago', lastFireProject: 'Peachtree Edge Data',
    projectsAssigned: ['Peachtree Edge Data', 'Sonoran Compute Park'],
    projectsTriggered: [],
    savedValue: '$3M avg',
    corpusMatches: [
      { project: 'Aligned Dallas Build', year: 2022, outcome: 'MEP surge crew at week 28 — 0 commissioning impact', relevance: 89 },
      { project: 'QTS Richmond',         year: 2020, outcome: 'MEP lag unaddressed — 3-week slip, $6M cost', relevance: 84 },
    ],
  },
  {
    id: 'COMM-001', name: 'IST Sequence Conflict', category: 'Commission', status: 'Triggered',
    description: 'Detects when integrated systems testing creates mechanical conflicts — most commonly CRAC commissioning windows overlapping with electrical IST sequencing.',
    trigger: 'IST window overlap > 5 days between any two mechanical/electrical systems on critical path',
    corpusBasis: '47 commissioning sequences. IST conflicts are the #1 cause of final-mile slippage — detected in 23% of projects without structured sequencing.',
    confidenceThreshold: 88,
    recommendedAction: 'Authorize parallel IST tracks. Dedicate separate crews to conflicting systems. Mobilize surge capacity if SLA window < 45 days.',
    lastFired: '1 day ago', lastFireProject: 'Lone Star AI Campus',
    projectsAssigned: ['Lone Star AI Campus'],
    projectsTriggered: ['Lone Star AI Campus'],
    savedValue: '$42M',
    corpusMatches: [
      { project: 'eBay Salt Lake City', year: 2018, outcome: 'Parallel IST resolved 21-day slip in 8 days via surge protocol', relevance: 98 },
      { project: 'Switch LAS 4',        year: 2020, outcome: 'CRAC/electrical conflict — 14-day slip, $28M SLA penalty', relevance: 91 },
      { project: 'Aligned PHX 2',       year: 2022, outcome: 'Surge crew protocol — 11 days recovered at $2.4M cost', relevance: 87 },
    ],
  },
  {
    id: 'COMM-002', name: 'P1 Punch List · SLA Window', category: 'Commission', status: 'Triggered',
    description: 'Monitors open P1 punch list items relative to remaining SLA window. Fires when projected close rate puts SLA at risk.',
    trigger: 'P1 punch close rate insufficient to clear list before SLA date at current velocity',
    corpusBasis: '41 commissioning closeouts tracked. P1 item count at T-45 days is the strongest leading indicator of SLA breach.',
    confidenceThreshold: 91,
    recommendedAction: 'Convene daily P1 war room. Assign owners to every open item. SLA breach cost is non-linear — early intervention is 10× cheaper.',
    lastFired: '1 day ago', lastFireProject: 'Lone Star AI Campus',
    projectsAssigned: ['Lone Star AI Campus'],
    projectsTriggered: ['Lone Star AI Campus'],
    savedValue: 'Included in COMM-001',
    corpusMatches: [
      { project: 'Aligned Dallas 2022', year: 2022, outcome: 'Daily war room at T-38 — all P1s cleared, no SLA breach', relevance: 93 },
      { project: 'CyrusOne Phoenix',    year: 2021, outcome: '41 P1s at T-30 — SLA breached by 9 days, $18M penalty', relevance: 88 },
    ],
  },
  {
    id: 'COMM-003', name: 'Surge Crew Authorization', category: 'Commission', status: 'Active',
    description: 'Pre-authorizes surge crew mobilization when commissioning risk drops below threshold. Removes decision latency from the highest-cost phase.',
    trigger: 'Commission risk score < 45 AND days to SLA < 50',
    corpusBasis: 'Decision latency analysis across 19 commissioning interventions. Average time to surge authorization: 4.2 days without pre-auth, 0.3 days with.',
    confidenceThreshold: 90,
    recommendedAction: 'Pre-authorize surge crew to mobilize within 24 hours when both trigger conditions are met.',
    lastFired: 'Not yet fired', lastFireProject: '—',
    projectsAssigned: ['Lone Star AI Campus'],
    projectsTriggered: [],
    savedValue: '3.9 days decision latency',
    corpusMatches: [
      { project: 'eBay SLC 2018',    year: 2018, outcome: 'Pre-authorized surge crew mobilized in 6 hours — 19 days recovered', relevance: 96 },
      { project: 'Aligned PHX 2022', year: 2022, outcome: '4-day authorization delay cost 8 recoverable days', relevance: 89 },
    ],
  },
  {
    id: 'CPTL-001', name: 'Exit Window Optimization', category: 'Capital', status: 'Active',
    description: 'Models hyperscaler capex cycles and infrastructure fund deployment windows to identify peak exit timing.',
    trigger: 'Demand cycle model scores exit window ≥ 85 AND governance readiness ≥ 80',
    corpusBasis: '12 comparable transactions modeled: Switch, QTS, CyrusOne, Aligned, Iron Mountain REIT. Timing premium: 15-22% average.',
    confidenceThreshold: 88,
    recommendedAction: 'Initiate sale process 6 months before optimal window. PUE optimization adds 2-4% to exit multiple.',
    lastFired: '7 days ago', lastFireProject: 'Virginia Hyperscale Cluster',
    projectsAssigned: ['Virginia Hyperscale Cluster'],
    projectsTriggered: [],
    savedValue: '$180M+ uplift',
    corpusMatches: [
      { project: 'Switch REIT IPO',     year: 2017, outcome: '11× EBITDA — peak demand cycle exit', relevance: 96 },
      { project: 'QTS Blackstone',      year: 2021, outcome: '28× EBITDA — AI demand surge premium', relevance: 92 },
      { project: 'Aligned Blackrock',   year: 2025, outcome: '$40B — sovereign AI infrastructure premium', relevance: 89 },
    ],
  },
  {
    id: 'CPTL-002', name: 'PUE Optimization Opportunity', category: 'Capital', status: 'Active',
    description: 'Identifies when PUE is above corpus benchmark for asset class and cooling configuration. Quantifies exit valuation impact.',
    trigger: 'PUE actual > PUE corpus benchmark by > 0.04 for cooling configuration',
    corpusBasis: '28 operating assets with PUE tracking. RL thermal controls show average 0.06 PUE improvement at $380K/year savings.',
    confidenceThreshold: 85,
    recommendedAction: 'Implement RL thermal controls 6+ months before exit process. Zero downtime. NPV of optimization vs cost is typically 8-12× at exit.',
    lastFired: '7 days ago', lastFireProject: 'Virginia Hyperscale Cluster',
    projectsAssigned: ['Virginia Hyperscale Cluster'],
    projectsTriggered: [],
    savedValue: '$2.8M exit uplift',
    corpusMatches: [
      { project: 'QTS Richmond Ops',   year: 2020, outcome: 'RL controls: PUE 1.42→1.36, $410K/yr, +2.1% multiple', relevance: 92 },
      { project: 'Aligned Dallas Ops', year: 2023, outcome: 'PUE optimization 6mo before sale: +$3.1M exit value', relevance: 89 },
    ],
  },
  {
    id: 'CPTL-003', name: 'Budget Variance Escalation', category: 'Capital', status: 'Monitoring',
    description: 'Escalates when project budget variance exceeds thresholds by phase. Early variance is manageable — late variance compounds.',
    trigger: 'Variance > +1.5% (Site/Design) | > +2.5% (Build) | > +1.0% (Commission)',
    corpusBasis: '52 project completions with budget tracking. Commission phase variance has highest NPV impact due to SLA penalty exposure.',
    confidenceThreshold: 82,
    recommendedAction: 'Convene budget review within 5 business days. Commission phase: prioritize recovery over cost control.',
    lastFired: '4 days ago', lastFireProject: 'Lone Star AI Campus',
    projectsAssigned: ['Lone Star AI Campus', 'Peachtree Edge Data', 'Midwest Sovereign Exchange'],
    projectsTriggered: [],
    savedValue: '$2M avg',
    corpusMatches: [
      { project: 'Aligned build portfolio', year: 2022, outcome: 'Early detection: 94% of projects held within 3% of budget', relevance: 88 },
    ],
  },
  {
    id: 'EXIT-001', name: 'Governance Readiness Score', category: 'Exit', status: 'Active',
    description: 'Tracks IPO and strategic transaction governance readiness: board composition, audit cleanliness, ESG reporting, financial model normalization.',
    trigger: 'Exit window within 18 months AND governance score < 80',
    corpusBasis: '9 completed exit transactions. Governance gaps in due diligence add 60-90 days to close and reduce multiple by 4-8%.',
    confidenceThreshold: 90,
    recommendedAction: 'Begin governance prep 18 months before target exit. Prioritize: independent board seats, 3-year audited financials, GRESB Tier 1.',
    lastFired: '14 days ago', lastFireProject: 'Virginia Hyperscale Cluster',
    projectsAssigned: ['Virginia Hyperscale Cluster'],
    projectsTriggered: [],
    savedValue: 'Multiple protection: 4-8%',
    corpusMatches: [
      { project: 'Switch IPO 2017',    year: 2017, outcome: 'Clean governance — IPO at top of range', relevance: 94 },
      { project: 'QTS strategic 2021', year: 2021, outcome: '30-day DD extension due to board structure — $120M haircut', relevance: 89 },
    ],
  },
  {
    id: 'EXIT-002', name: 'Strategic Buyer Identification', category: 'Exit', status: 'Active',
    description: 'Monitors infrastructure fund deployment mandates, REIT capital raise cycles, and hyperscaler M&A signals to identify buyers before process launch.',
    trigger: 'Asset meets exit readiness threshold AND ≥2 identified buyers with active mandates',
    corpusBasis: 'Buyer mandate tracking across 14 infrastructure funds, 6 REITs, and 4 hyperscaler corporate development teams.',
    confidenceThreshold: 78,
    recommendedAction: 'Build relationships with identified buyers 12+ months before process. Provide data room access 6 months early for competitive tension.',
    lastFired: '7 days ago', lastFireProject: 'Virginia Hyperscale Cluster',
    projectsAssigned: ['Virginia Hyperscale Cluster'],
    projectsTriggered: [],
    savedValue: 'Process premium: 8-15%',
    corpusMatches: [
      { project: 'Aligned exit 2025', year: 2025, outcome: '3 competing bidders → $40B — 22% premium vs single-buyer', relevance: 91 },
      { project: 'CyrusOne 2021',     year: 2021, outcome: 'Pre-identified buyers — 40-day faster close', relevance: 86 },
    ],
  },
];

const POLICY_STATS = [
  { label: 'Total Policies', value: POLICIES.length.toString(),                                          color: T.textPrimary },
  { label: 'Active',         value: POLICIES.filter(p => p.status === 'Active').length.toString(),        color: T.greenLight  },
  { label: 'Triggered',      value: POLICIES.filter(p => p.status === 'Triggered').length.toString(),     color: T.redLight    },
  { label: 'Monitoring',     value: POLICIES.filter(p => p.status === 'Monitoring').length.toString(),    color: T.amberLight  },
  { label: 'Value Protected',value: '$280M+',                                                            color: T.gold        },
];

// ════════════════════════════════════════════════════════════════════════════
// POLICY CARD
// ════════════════════════════════════════════════════════════════════════════
const PolicyCard = ({ policy, onClick, isSelected }) => {
  const cat = CAT(policy.category);
  const st = STATUS(policy.status);

  return (
    <div onClick={() => onClick(policy)}
      style={{
        background: T.bgCard, border: `1px solid ${isSelected ? T.goldBorder : T.border}`,
        borderRadius: 8, padding: 16, cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
        boxShadow: isSelected ? `0 0 0 2px ${T.goldBorder}` : '0 1px 4px rgba(26,24,20,0.05)',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderStrong; e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,24,20,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = isSelected ? T.goldBorder : T.border; e.currentTarget.style.boxShadow = isSelected ? `0 0 0 2px ${T.goldBorder}` : '0 1px 4px rgba(26,24,20,0.05)'; }}
    >
      {/* Category color rule at top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: cat.color, borderRadius: '8px 8px 0 0', opacity: 0.7 }} />

      {/* ID + Status row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{
          fontSize: 10, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.06em',
          padding: '3px 8px', borderRadius: 4,
          background: cat.bg, border: `1px solid ${cat.border}`, color: cat.color,
        }}>{policy.id}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 12, background: st.bg, border: `1px solid ${st.border}` }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: st.dot, animation: policy.status === 'Triggered' ? 'pulse 1.5s infinite' : 'none' }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: st.text, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{st.label}</span>
        </div>
      </div>

      {/* Name + Category */}
      <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif', marginBottom: 3 }}>{policy.name}</div>
      <div style={{ fontSize: 10, fontWeight: 600, color: cat.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{policy.category}</div>

      {/* Description */}
      <p style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.55, margin: '0 0 10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{policy.description}</p>

      {/* Trigger */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
        <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Trigger condition</div>
        <div style={{ fontSize: 10, color: T.textPrimary, fontFamily: 'monospace', lineHeight: 1.4 }}>{policy.trigger}</div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div>
            <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Assigned</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{policy.projectsAssigned.length}</div>
          </div>
          {policy.projectsTriggered.length > 0 && (
            <div>
              <div style={{ fontSize: 8, color: T.redLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Triggered</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.redLight, fontFamily: 'Georgia, serif' }}>{policy.projectsTriggered.length}</div>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Value protected</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.greenLight, fontFamily: 'Georgia, serif' }}>{policy.savedValue}</div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// POLICY DETAIL PANEL
// ════════════════════════════════════════════════════════════════════════════
const PolicyPanel = ({ policy, onClose }) => {
  const cat = CAT(policy.category);
  const st = STATUS(policy.status);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,24,20,0.3)' }} onClick={onClose} />
      <div style={{
        width: '100%', maxWidth: 460, background: T.bgCard, borderLeft: `1px solid ${T.border}`,
        height: '100%', overflowY: 'auto', position: 'relative', zIndex: 1,
        animation: 'slideInRight 0.22s ease-out', boxShadow: '-8px 0 32px rgba(26,24,20,0.10)',
      }} onClick={e => e.stopPropagation()}>

        {/* Category color rule */}
        <div style={{ height: 3, background: cat.color }} />

        {/* Header */}
        <div style={{ padding: '20px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.06em', padding: '4px 10px', borderRadius: 4, background: cat.bg, border: `1px solid ${cat.border}`, color: cat.color }}>{policy.id}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 12, background: st.bg, border: `1px solid ${st.border}` }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: st.dot, animation: policy.status === 'Triggered' ? 'pulse 1.5s infinite' : 'none' }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: st.text, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{st.label}</span>
              </div>
            </div>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.textSecondary, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, fontFamily: 'Georgia, serif', marginBottom: 3 }}>{policy.name}</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: cat.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{policy.category} Policy</div>
          <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, margin: 0 }}>{policy.description}</p>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Trigger + Corpus basis */}
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 8 }}>Trigger Condition</div>
            <div style={{ fontSize: 11, color: T.textPrimary, fontFamily: 'monospace', lineHeight: 1.5, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 5, padding: '8px 10px' }}>{policy.trigger}</div>
          </div>

          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 6 }}>Corpus Basis</div>
            <p style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.6, margin: 0 }}>{policy.corpusBasis}</p>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              { label: 'Confidence',    value: `${policy.confidenceThreshold}%`, color: T.textPrimary },
              { label: 'Assigned',      value: policy.projectsAssigned.length,    color: T.textPrimary },
              { label: 'Triggered now', value: policy.projectsTriggered.length,   color: policy.projectsTriggered.length > 0 ? T.redLight : T.textMuted },
            ].map((s, i) => (
              <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Last fired */}
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 8 }}>Last Fired</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{policy.lastFireProject}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{policy.lastFired}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Protected</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.greenLight, fontFamily: 'Georgia, serif' }}>{policy.savedValue}</div>
              </div>
            </div>
          </div>

          {/* Corpus matches */}
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 8 }}>Corpus Matches</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {policy.corpusMatches.map((m, i) => (
                <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 7, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{m.project}</span>
                      <span style={{ fontSize: 10, color: T.textMuted }}>{m.year}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 50, height: 3, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: T.goldLight, width: `${m.relevance}%`, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.gold }}>{m.relevance}%</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{m.outcome}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended action */}
          <div style={{ background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 8 }}>Recommended Action</div>
            <p style={{ fontSize: 12, color: T.textPrimary, lineHeight: 1.6, margin: 0 }}>{policy.recommendedAction}</p>
          </div>

          {/* Assigned projects */}
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 8 }}>Assigned Projects</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {policy.projectsAssigned.map((proj, i) => {
                const fired = policy.projectsTriggered.includes(proj);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 6,
                    background: fired ? T.redBg : T.bg,
                    border: `1px solid ${fired ? T.redBorder : T.border}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: fired ? T.redLight : T.greenLight, animation: fired ? 'pulse 1.5s infinite' : 'none' }} />
                      <span style={{ fontSize: 12, color: T.textPrimary }}>{proj}</span>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: fired ? T.redLight : T.greenLight }}>
                      {fired ? 'Triggered' : 'Monitoring'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ textAlign: 'center', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', paddingBottom: 4 }}>
            Axiom Policy Engine · $5B corpus basis · 15+ hyperscale builds
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════════════════
const AxiomPoliciesV3 = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [view, setView] = useState('table');

  const filtered = POLICIES.filter(p => {
    const catMatch = activeCategory === 'All' || p.category === activeCategory;
    const stMatch  = statusFilter  === 'All' || p.status   === statusFilter;
    return catMatch && stMatch;
  });

  const triggeredCount = POLICIES.filter(p => p.status === 'Triggered').length;

  return (
    <div style={{ minHeight: '100vh', width: '100%', background: T.bg, fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif", color: T.textPrimary }}>

      {/* Header */}
      <div style={{ background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '0 24px', position: 'relative', boxShadow: '0 1px 4px rgba(26,24,20,0.06)' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.goldLight} 0%, ${T.gold} 30%, transparent 70%)` }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, padding: '22px 0 18px' }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.25em', marginBottom: 6 }}>Axiom Intelligence Platform</div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 36, height: 36, borderRadius: 6, background: T.goldBg, border: `1px solid ${T.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/>
                </svg>
              </span>
              Policy Engine
            </h1>
            <p style={{ fontSize: 12, color: T.textSecondary, margin: '5px 0 0' }}>Governed intelligence · Every signal has a policy · Every policy has a corpus basis</p>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {POLICY_STATS.map((s, i) => (
              <div key={s.label} style={{ textAlign: 'right', padding: '0 24px', borderLeft: i > 0 ? `1px solid ${T.border}` : 'none' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: 'Georgia, serif' }}>{s.value}</div>
                <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Triggered alert */}
        {triggeredCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 7, padding: '10px 14px', marginBottom: 18 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.redLight, animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: T.red, fontWeight: 600 }}>{triggeredCount} policies currently triggered — requiring intervention</span>
            <button onClick={() => { setActiveCategory('All'); setStatusFilter('Triggered'); }}
              style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: T.redLight, background: 'none', border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Show triggered →
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, background: T.bgCard }}>

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 2, background: T.bg, borderRadius: 8, padding: 4, border: `1px solid ${T.border}` }}>
          {CATEGORIES.map(cat => {
            const acc = cat !== 'All' ? CAT(cat) : null;
            const isActive = activeCategory === cat;
            const count = cat === 'All' ? POLICIES.length : POLICIES.filter(p => p.category === cat).length;
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, transition: 'all 0.15s',
                  background: isActive ? T.bgCard : 'transparent',
                  color: isActive ? (acc ? acc.color : T.textPrimary) : T.textMuted,
                  boxShadow: isActive ? '0 1px 3px rgba(26,24,20,0.08)' : 'none',
                  borderBottom: isActive && acc ? `2px solid ${acc.color}` : '2px solid transparent',
                }}>
                {acc && <div style={{ width: 5, height: 5, borderRadius: '50%', background: acc.dot }} />}
                {cat}
                <span style={{ fontSize: 9, color: isActive ? T.textSecondary : T.textMuted }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Status filter + view toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['All', 'Triggered', 'Active', 'Monitoring'].map(s => {
              const sc = s !== 'All' ? STATUS(s) : null;
              const isActive = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    border: `1px solid ${isActive ? (sc ? sc.border : T.borderStrong) : 'transparent'}`,
                    background: isActive ? (sc ? sc.bg : T.bg) : 'transparent',
                    color: isActive ? (sc ? sc.text : T.textPrimary) : T.textMuted,
                    transition: 'all 0.15s',
                  }}>
                  {sc && <div style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot }} />}
                  {s}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 2, background: T.bg, borderRadius: 6, padding: 3, border: `1px solid ${T.border}` }}>
            {['cards', 'table'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{
                  padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                  background: view === v ? T.bgCard : 'transparent',
                  color: view === v ? T.textPrimary : T.textMuted,
                  boxShadow: view === v ? '0 1px 3px rgba(26,24,20,0.08)' : 'none',
                  transition: 'all 0.15s',
                }}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Policy grid / table */}
      <div style={{ padding: '20px 24px 40px' }}>
        {view === 'table' && filtered.length > 0 && (
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}`, marginBottom: 16, background: T.bgCard, boxShadow: '0 1px 4px rgba(26,24,20,0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.bgElevated, borderBottom: `1px solid ${T.border}` }}>
                  {['ID', 'Policy', 'Category', 'Status', 'Confidence', 'Last Fired', 'Projects', 'Saved Value', 'Trigger'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const acc = CAT(p.category);
                  const sc = STATUS(p.status);
                  return (
                    <tr key={p.id}
                      style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer', background: i % 2 === 0 ? T.bgCard : T.bgElevated }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bgHover}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.bgCard : T.bgElevated}
                      onClick={() => setSelectedPolicy(prev => prev?.id === p.id ? null : p)}>
                      <td style={{ padding: '8px 14px', fontSize: 11, fontFamily: 'monospace', color: T.textMuted }}>{p.id}</td>
                      <td style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, color: T.textPrimary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</td>
                      <td style={{ padding: '8px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: acc.bg, color: acc.color, border: `1px solid ${acc.border}` }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: acc.dot }} />{p.category}
                        </span>
                      </td>
                      <td style={{ padding: '8px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: sc.bg || 'transparent', color: sc.text || T.textMuted, border: `1px solid ${sc.border || T.border}` }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot || T.textMuted }} />{p.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 14px', fontSize: 11, color: T.textSecondary }}>{p.confidenceThreshold}%</td>
                      <td style={{ padding: '8px 14px', fontSize: 11, color: T.textMuted }}>{p.lastFired || '\u2014'}</td>
                      <td style={{ padding: '8px 14px', fontSize: 11, color: T.textSecondary }}>{p.projectsAssigned?.length || 0}</td>
                      <td style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: T.green }}>{p.savedValue || '\u2014'}</td>
                      <td style={{ padding: '8px 14px', fontSize: 11, color: T.textMuted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.trigger}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {view === 'cards' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {filtered.map(policy => (
              <PolicyCard key={policy.id} policy={policy}
                onClick={p => setSelectedPolicy(prev => prev?.id === p.id ? null : p)}
                isSelected={selectedPolicy?.id === policy.id}
              />
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.textMuted, fontSize: 13 }}>No policies match current filters</div>
        )}
      </div>

      {selectedPolicy && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSelectedPolicy(null)} />
          <PolicyPanel policy={selectedPolicy} onClose={() => setSelectedPolicy(null)} />
        </>
      )}

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pulse        { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      `}</style>
    </div>
  );
};

export default AxiomPoliciesV3;
